import React, { useRef, useState } from 'react'
import {
  Accordion,
  Button,
  CenteredSpinner,
  Col,
  IconInput,
  Row,
  useServerPagination
} from 'react-angle-dashboard-components'
import { Container } from 'react-bootstrap'
import { Pagination } from '../../components/Pagination/Pagination'
import { cptService as CPTService } from '../../services'
import { searchBarStyles, SearchButton, SearchSection, Title } from './styles'
import { IconUpArrow } from './vector'
import { OurProviderSection } from './OurProviders'
import { ProcedureDetails } from './ProcedureDetails'
import { CTPProcedure } from '../../services/cpt'

const SearchCpt: React.FC = () => {
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<string | null>(null)

  const {
    paginationConfig,
    queryResult: { data, isLoading }
  } = useServerPagination(['cpt.search', search], ({ pageLimit, pageNumber }) =>
    CPTService.getProcedures({
      query: !!search ? search : undefined,
      page_number: pageNumber,
      page_limit: pageLimit
    })
  )

  const inputSearchRef = useRef<HTMLInputElement | null>(null)

  const toggleSelect = (id: string) =>
    setSelected((current) => (id === current ? null : id))

  return (
    <div>
      <SearchSection>
        <Container fluid="md" className="text-center px-2">
          <Title>Authorization Search</Title>

          <IconInput
            ref={inputSearchRef}
            icon="icon-search"
            containerClassName="border"
            inlineClassName="d-block w-100"
            className="fs-16"
            noShadow
            placeholder="Search for procedure by code or description"
            barStyle={searchBarStyles.bar}
            onChange={(e: any) => (inputSearchRef.current = e.target?.value)}
            appendIcon="-"
            onKeyUp={(e: { key: string }) => {
              if (
                e.key === 'Enter' &&
                typeof inputSearchRef.current === 'string'
              ) {
                setSearch(inputSearchRef.current)
              }
            }}
            appendNode={
              <SearchButton
                size="sm"
                className="border d-none d-md-inline"
                key="search-button"
                onClick={() =>
                  typeof inputSearchRef.current === 'string' &&
                  setSearch(inputSearchRef.current)
                }
              >
                <span className="d-none d-sm-inline">Search</span>
              </SearchButton>
            }
          />
          <span>
            <SearchButton
              block
              size="sm"
              className="d-inline d-md-none"
              key="search-button"
              onClick={() =>
                typeof inputSearchRef.current === 'string' &&
                setSearch(inputSearchRef.current)
              }
            >
              <span>Search</span>
            </SearchButton>
          </span>
        </Container>
      </SearchSection>

      {!search ? (
        <OurProviderSection />
      ) : (
        <Container className="mt-4">
          <Row className="mx-0" style={{ flex: 1 }}>
            <Col xs={12} className="py-3 px-1">
              <Row className="text-muted fs-18 d-none d-sm-flex">
                <Col xs="2">Code</Col>
                <Col>Description</Col>
                <Col xs="2"></Col>
              </Row>

              <div
                style={{
                  background: 'transparent'
                }}
              >
                {isLoading ? (
                  <Row data-testid="loading-tbody">
                    <Col>
                      <div className="text-center py-5">
                        <CenteredSpinner />
                      </div>
                    </Col>
                  </Row>
                ) : null}
                {!data?.procedures?.length && !isLoading ? (
                  <Row data-testid="loading-tbody">
                    <Col>
                      <h5 className="text-left text-muted pl-0 py-4">
                        No procedures were found.
                      </h5>
                    </Col>
                  </Row>
                ) : (
                  <Accordion>
                    {data?.procedures?.map((procedure: CTPProcedure) => {
                      return (
                        <div className="my-3" key={procedure.procedure_code}>
                          <Accordion.Toggle
                            role="button"
                            eventKey={procedure.procedure_code}
                            as={Row}
                            onClick={() =>
                              toggleSelect(procedure.procedure_code)
                            }
                            className={`border py-2 ${
                              procedure.procedure_code === selected
                                ? 'bg-light rounded-top'
                                : 'rounded'
                            }`}
                          >
                            <Col
                              sm="2"
                              xs="12"
                              className="fs-14 d-flex align-items-center"
                            >
                              <b className="text-primary">
                                {procedure.procedure_code}
                              </b>
                            </Col>
                            <Col className="d-flex align-items-center">
                              <b className="text-muted">
                                {procedure.description}
                              </b>
                            </Col>
                            <Col xs="2">
                              <Button
                                variant="link"
                                className="text-primary py-1 px-1"
                                role="button"
                              >
                                {procedure.procedure_code === selected ? (
                                  <>
                                    <span className="d-none d-sm-inline">
                                      See Less
                                    </span>{' '}
                                    <IconUpArrow
                                      style={{ marginLeft: '12px' }}
                                    />
                                  </>
                                ) : (
                                  <>
                                    <span className="d-none d-sm-inline">
                                      See More
                                    </span>{' '}
                                    <i
                                      style={{ top: 2, position: 'relative' }}
                                      className="ml-2 icon-down-arrow"
                                    />
                                  </>
                                )}
                              </Button>
                            </Col>
                          </Accordion.Toggle>
                          <Accordion.Collapse
                            eventKey={procedure.procedure_code}
                          >
                            <ProcedureDetails procedure={procedure} />
                          </Accordion.Collapse>
                        </div>
                      )
                    })}
                  </Accordion>
                )}
              </div>
            </Col>
          </Row>
          {paginationConfig.totalItems > paginationConfig.pageLimit && (
            <Pagination
              totalPages={paginationConfig.totalPages}
              totalItems={paginationConfig.totalItems}
              currentPage={paginationConfig.currentPage}
              pageLimit={paginationConfig.pageLimit}
              setPage={paginationConfig.setPage}
            />
          )}
        </Container>
      )}
    </div>
  )
}

export default SearchCpt
